import type { StateCreator } from 'zustand';
import {
  lpAnalyticsApi,
  type DailyStats,
  type Session,
  type ABVariant,
  type BridgeRow,
  type CreateVariantInput,
  type PostHogStats,
  type PostHogLive,
  type SiteFilter,
  type LPInsight,
  type LPBooking,
  type LPLink,
  type UtmKanal,
  type UtmZiel,
  type CreateLinkInput,
} from '../services/lpAnalyticsApi';

export interface LPAnalyticsSlice {
  lpStats: DailyStats[];
  lpStatsTotals: {
    sessions: number;
    conversions: number;
    bounce_rate: number;
    avg_time_on_page: number;
    traffic_sources: Record<string, number>;
    device_split: Record<string, number>;
    site_split: Record<string, number>;
    page_split: Record<string, number>;
  } | null;
  lpSessions: Session[];
  lpSessionsTotal: number;
  lpABTests: ABVariant[];
  lpBridge: BridgeRow[];
  lpPostHogStats: PostHogStats | null;
  lpPostHogLive: PostHogLive | null;
  // Filtert Sessions-Liste + PostHog-Sektion nach Domain. Betrifft NICHT lpStats/lpStatsTotals
  // (kommt aus lp_daily_stats, das noch nicht nach Site aggregiert -- site_split darin zeigt
  // die Verteilung trotzdem, unabhaengig vom Filter).
  lpSiteFilter: SiteFilter;
  lpInsights: LPInsight[];
  lpInsightGenerating: boolean;
  lpInsightError: string | null;
  lpBookings: LPBooking[];
  lpLinks: LPLink[];
  lpKanaele: UtmKanal[];
  lpZiele: UtmZiel[];
  lpLoading: boolean;
  lpError: string | null;

  setLPSiteFilter: (site: SiteFilter) => void;
  fetchLPStats: (start: string, end: string) => Promise<void>;
  fetchLPSessions: (page: number) => Promise<void>;
  fetchLPABTests: () => Promise<void>;
  fetchLPBridge: (days?: number) => Promise<void>;
  fetchLPPostHogStats: (start: string, end: string) => Promise<void>;
  fetchLPPostHogLive: () => Promise<void>;
  fetchLPInsights: () => Promise<void>;
  generateLPInsight: () => Promise<void>;
  dismissLPInsight: (id: number) => Promise<void>;
  fetchLPBookings: (site?: 'leitkraft' | 'leitscope') => Promise<void>;
  createLPVariant: (data: CreateVariantInput) => Promise<void>;
  updateLPVariant: (id: string, data: { status: 'active' | 'inactive' }) => Promise<void>;
  fetchLPKanaele: () => Promise<void>;
  fetchLPLinks: () => Promise<void>;
  /** Gibt den angelegten Eintrag zurueck, damit der Aufrufer den Link sofort kopieren kann. */
  createLPLink: (data: CreateLinkInput) => Promise<LPLink | null>;
}

export const createLPAnalyticsSlice: StateCreator<LPAnalyticsSlice> = (set, get) => ({
  lpStats: [],
  lpStatsTotals: null,
  lpSessions: [],
  lpSessionsTotal: 0,
  lpABTests: [],
  lpBridge: [],
  lpPostHogStats: null,
  lpPostHogLive: null,
  lpSiteFilter: 'all',
  lpInsights: [],
  lpInsightGenerating: false,
  lpInsightError: null,
  lpBookings: [],
  lpLinks: [],
  lpKanaele: [],
  lpZiele: [],
  lpLoading: false,
  lpError: null,

  setLPSiteFilter: (site) => set({ lpSiteFilter: site }),

  fetchLPStats: async (start, end) => {
    set({ lpLoading: true, lpError: null });
    try {
      const res = await lpAnalyticsApi.getStats(start, end);
      set({ lpStats: res.daily ?? [], lpStatsTotals: res.totals ?? null, lpLoading: false });
    } catch (e) {
      set({ lpLoading: false, lpError: e instanceof Error ? e.message : 'Error' });
    }
  },

  fetchLPSessions: async (page) => {
    set({ lpLoading: true, lpError: null });
    try {
      const res = await lpAnalyticsApi.getSessions(page, get().lpSiteFilter);
      set({ lpSessions: res.sessions ?? [], lpSessionsTotal: res.total ?? 0, lpLoading: false });
    } catch (e) {
      set({ lpLoading: false, lpError: e instanceof Error ? e.message : 'Error' });
    }
  },

  fetchLPABTests: async () => {
    set({ lpLoading: true, lpError: null });
    try {
      const res = await lpAnalyticsApi.getABTests();
      set({ lpABTests: res.variants ?? [], lpLoading: false });
    } catch (e) {
      set({ lpLoading: false, lpError: e instanceof Error ? e.message : 'Error' });
    }
  },

  fetchLPBridge: async (days = 30) => {
    set({ lpLoading: true, lpError: null });
    try {
      const rows = await lpAnalyticsApi.getBridge(days);
      set({ lpBridge: rows ?? [], lpLoading: false });
    } catch (e) {
      set({ lpLoading: false, lpError: e instanceof Error ? e.message : 'Error' });
    }
  },

  fetchLPPostHogStats: async (start, end) => {
    // Eigener, stiller Fehlerpfad: PostHog ist eine optionale Zusatzsektion neben der eigenen
    // Auswertung (lpStats) -- ein PostHog-Ausfall darf das restliche Dashboard nicht blockieren.
    try {
      const res = await lpAnalyticsApi.getPostHogStats(start, end, get().lpSiteFilter);
      set({ lpPostHogStats: res });
    } catch {
      set({ lpPostHogStats: null });
    }
  },

  fetchLPPostHogLive: async () => {
    // Selber stiller Fehlerpfad wie fetchLPPostHogStats -- wird per Intervall gepollt,
    // ein einzelner Ausfall soll das Widget nicht auf einen Fehlerzustand kippen lassen.
    try {
      const res = await lpAnalyticsApi.getPostHogLive(5, get().lpSiteFilter);
      set({ lpPostHogLive: res });
    } catch {
      set({ lpPostHogLive: null });
    }
  },

  fetchLPInsights: async () => {
    try {
      const res = await lpAnalyticsApi.getInsights();
      set({ lpInsights: res.insights ?? [] });
    } catch {
      set({ lpInsights: [] });
    }
  },

  generateLPInsight: async () => {
    set({ lpInsightGenerating: true, lpInsightError: null });
    try {
      await lpAnalyticsApi.generateInsight(get().lpSiteFilter);
      const res = await lpAnalyticsApi.getInsights();
      set({ lpInsights: res.insights ?? [], lpInsightGenerating: false });
    } catch (e) {
      set({
        lpInsightGenerating: false,
        lpInsightError: e instanceof Error ? e.message : 'Diagnose fehlgeschlagen',
      });
    }
  },

  dismissLPInsight: async (id) => {
    try {
      await lpAnalyticsApi.dismissInsight(id);
      const res = await lpAnalyticsApi.getInsights();
      set({ lpInsights: res.insights ?? [] });
    } catch {
      // stiller Fehlerpfad -- Insight bleibt sichtbar, kann erneut versucht werden
    }
  },

  fetchLPBookings: async (site) => {
    set({ lpLoading: true, lpError: null });
    try {
      const res = await lpAnalyticsApi.getBookings(site);
      set({ lpBookings: res.bookings ?? [], lpLoading: false });
    } catch (e) {
      set({ lpLoading: false, lpError: e instanceof Error ? e.message : 'Error' });
    }
  },

  createLPVariant: async (data) => {
    set({ lpLoading: true, lpError: null });
    try {
      await lpAnalyticsApi.createVariant(data);
      const res = await lpAnalyticsApi.getABTests();
      set({ lpABTests: res.variants ?? [], lpLoading: false });
    } catch (e) {
      set({ lpLoading: false, lpError: e instanceof Error ? e.message : 'Error' });
    }
  },

  updateLPVariant: async (id, data) => {
    set({ lpLoading: true, lpError: null });
    try {
      await lpAnalyticsApi.updateVariant(id, data);
      const res = await lpAnalyticsApi.getABTests();
      set({ lpABTests: res.variants ?? [], lpLoading: false });
    } catch (e) {
      set({ lpLoading: false, lpError: e instanceof Error ? e.message : 'Error' });
    }
  },

  fetchLPKanaele: async () => {
    try {
      const res = await lpAnalyticsApi.getKanaele();
      set({ lpKanaele: res.kanaele ?? [], lpZiele: res.ziele ?? [] });
    } catch (e) {
      set({ lpError: e instanceof Error ? e.message : 'Error' });
    }
  },

  fetchLPLinks: async () => {
    set({ lpLoading: true, lpError: null });
    try {
      const res = await lpAnalyticsApi.getLinks();
      set({ lpLinks: res.links ?? [], lpLoading: false });
    } catch (e) {
      set({ lpLoading: false, lpError: e instanceof Error ? e.message : 'Error' });
    }
  },

  createLPLink: async (data) => {
    set({ lpLoading: true, lpError: null });
    try {
      const eintrag = await lpAnalyticsApi.createLink(data);
      const res = await lpAnalyticsApi.getLinks();
      set({ lpLinks: res.links ?? [], lpLoading: false });
      return eintrag;
    } catch (e) {
      set({ lpLoading: false, lpError: e instanceof Error ? e.message : 'Error' });
      return null;
    }
  },
});
